import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// Promise-based confirmation dialog. Any descendant of <ConfirmProvider> can
// call `const ok = await confirm({ title, message, ... })`. When `ok` is true
// the caller proceeds with the destructive action; when false the user backed
// out (cancel, backdrop click, or Escape).

export interface ConfirmOptions {
  readonly title: string;
  readonly message?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  readonly options: ConfirmOptions;
  readonly resolve: (ok: boolean) => void;
}

interface ConfirmProviderProps {
  readonly children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps): JSX.Element {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  function settle(ok: boolean): void {
    if (!pending) return;
    pending.resolve(ok);
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <ConfirmDialog
          options={pending.options}
          onCancel={() => settle(false)}
          onConfirm={() => settle(true)}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm must be used inside a ConfirmProvider');
  return fn;
}

interface ConfirmDialogProps {
  readonly options: ConfirmOptions;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function ConfirmDialog({ options, onCancel, onConfirm }: ConfirmDialogProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Cancel-by-default focus: if the user mashes Enter (or anywhere on the
  // dialog) we want the safe path, not the destructive one. The Confirm
  // button has its own deliberate handler.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  return (
    <DialogShell
      options={options}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelRef={cancelRef}
    />
  );
}

interface DialogShellProps extends ConfirmDialogProps {
  readonly cancelRef: React.RefObject<HTMLButtonElement>;
}

function DialogShell(props: DialogShellProps): JSX.Element {
  const { options, onCancel, onConfirm, cancelRef } = props;
  const confirmClass = options.destructive ? DESTRUCTIVE_CONFIRM_CLASS : SAFE_CONFIRM_CLASS;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onCancel}
        className="absolute inset-0 cursor-default bg-slate-900/60"
        tabIndex={-1}
      />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 id="confirm-title" className="text-lg font-semibold text-slate-900">
          {options.title}
        </h2>
        {options.message ? <p className="mt-2 text-sm text-slate-600">{options.message}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {options.cancelLabel ?? 'Cancel'}
          </button>
          <button type="button" onClick={onConfirm} className={confirmClass}>
            {options.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

const DESTRUCTIVE_CONFIRM_CLASS =
  'rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700';
const SAFE_CONFIRM_CLASS =
  'rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800';
